import React, { useRef } from "react";

import { FiSearch } from "react-icons/fi";
import {
	Input,
	InputGroup,
	InputLeftElement,
	Skeleton,
	VStack,
} from "@chakra-ui/react";

import { Modal } from "newui/components/ui/Modal";
import { useCompanySearch } from "../../hooks/useCompanySearch";
import { useSwitchCompany } from "../../hooks/useSwitchCompany";
import type { ExecutiveCompanyOption } from "../../types/ExecutiveCompanyOption.type";
import { BackToProcuraconButton } from "./BackToProcuraconButton";
import { CompanyResultRow } from "./CompanyResultRow";

interface CompanySearchModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const CompanySearchModal: React.FC<CompanySearchModalProps> = ({
	isOpen,
	onClose,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const { companies, isLoading, setSearchTerm, searchTerm } =
		useCompanySearch();
	const { switchTo, isLoading: isSwitching } = useSwitchCompany();

	const handleSelect = async (company: ExecutiveCompanyOption) => {
		await switchTo(company.id);
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const showPrompt = searchTerm.length < 2 && !isLoading;
	const showNoResults =
		searchTerm.length >= 2 && !isLoading && companies.length === 0;
	const showResults = searchTerm.length >= 2 && !isLoading && companies.length > 0;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Switch Company"
			closeOnEsc
			closeOnOverlayClick
			trapFocus
			size="lg"
		>
			<div className="space-y-4">
				<BackToProcuraconButton />

				<InputGroup>
					<InputLeftElement pointerEvents="none">
						<FiSearch className="text-gray-400" />
					</InputLeftElement>
					<Input
						ref={inputRef}
						placeholder="Search companies..."
						value={searchTerm}
						onChange={handleSearchChange}
						autoFocus
						minLength={2}
						disabled={isSwitching}
						data-testid="company-search-input"
					/>
				</InputGroup>

				<div className="min-h-[120px]" data-testid="company-search-results">
					{showPrompt && (
						<p className="py-8 text-center text-sm text-gray-500">
							Type at least 2 characters to search
						</p>
					)}

					{isLoading && (
						<VStack spacing={2} align="stretch">
							{[...Array(4)].map((_, i) => (
								<Skeleton key={i} height="52px" borderRadius="md" />
							))}
						</VStack>
					)}

					{showNoResults && (
						<p className="py-8 text-center text-sm text-gray-500">
							No companies found for &ldquo;{searchTerm}&rdquo;
						</p>
					)}

					{showResults && (
						<div className="divide-y divide-gray-100 rounded-md border border-gray-200">
							{companies.map((company) => (
								<CompanyResultRow
									key={company.id}
									company={company}
									onClick={handleSelect}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};
